/**
 * Currency Configuration
 * Defines currency-specific settings including minimum prices, symbols, and Stripe codes
 */

// Currency-specific configurations
export const CURRENCY_CONFIG = {
  USD: {
    symbol: '$',
    code: 'USD',
    minimumPrice: 4.00,
    locale: 'en-US',
    stripeCurrency: 'usd',
    country: 'US',
    name: 'US Dollar'
  },
  CAD: {
    symbol: '$',
    code: 'CAD',
    minimumPrice: 5.00,
    locale: 'en-CA',
    stripeCurrency: 'cad',
    country: 'CA',
    name: 'Canadian Dollar'
  },
  AUD: {
    symbol: 'A$',
    code: 'AUD',
    minimumPrice: 5.00,
    locale: 'en-AU',
    stripeCurrency: 'aud',
    country: 'AU',
    name: 'Australian Dollar'
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
    minimumPrice: 4.00,
    locale: 'en-IE',
    stripeCurrency: 'eur',
    country: 'IE',
    name: 'Euro'
  },
  GBP: {
    symbol: '£',
    code: 'GBP',
    minimumPrice: 3.00, // Keep existing GBP minimum
    locale: 'en-GB',
    stripeCurrency: 'gbp',
    country: 'GB',
    name: 'British Pound'
  },
  NZD: {
    symbol: 'NZ$',
    code: 'NZD',
    minimumPrice: 5.00,
    locale: 'en-NZ',
    stripeCurrency: 'nzd',
    country: 'NZ',
    name: 'New Zealand Dollar'
  }
};

/**
 * Get minimum price for a currency
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {number} Minimum price for the currency
 */
export const getMinimumPrice = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.minimumPrice || 3.00; // Default to £3.00 if currency not found
};

/**
 * Minimum price for catalogue rows where title and artist are both "test" (case-insensitive).
 * USD uses $1.00 for card-wallet / PayPal test payments; other currencies keep £0.50 equivalent.
 * @param {string} currency - Currency code (e.g. 'USD', 'GBP')
 * @returns {number}
 */
export const getTestSongMinimumPrice = (currency = 'GBP') => {
  const code = (currency || 'GBP').toUpperCase();
  if (code === 'USD') return 1.0;
  return 0.5;
};

/**
 * Get Stripe currency code (lowercase) for a currency
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {string} Stripe currency code (e.g., 'usd', 'gbp')
 */
export const getStripeCurrency = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.stripeCurrency || 'gbp'; // Default to GBP if currency not found
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {string} Currency symbol (e.g., '$', '£')
 */
export const getCurrencySymbol = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.symbol || '£'; // Default to £ if currency not found
};

/**
 * Get locale for a currency
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {string} Locale string (e.g., 'en-US', 'en-GB')
 */
export const getLocaleForCurrency = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.locale || 'en-GB'; // Default to en-GB if currency not found
};

/**
 * Get country code for a currency
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {string} Country code (e.g., 'US', 'GB')
 */
export const getCountryForCurrency = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.country || 'GB'; // Default to GB if currency not found
};

/**
 * Format minimum price for display
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {string} Formatted minimum price (e.g., '$4.00', '£3.00')
 */
export const formatMinimumPrice = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  if (!config) return '£3.00';
  
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(config.minimumPrice);
};

/**
 * Formatted minimum for "test"/"test" songs (see getTestSongMinimumPrice).
 */
export const formatTestSongMinimumPrice = (currency = 'GBP') => {
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  if (!config) return '£0.50';
  const amt = getTestSongMinimumPrice(currency);
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amt);
};

/**
 * Check if a price meets the minimum requirement for a currency
 * @param {number} price - Price to check
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP')
 * @returns {boolean} True if price meets minimum requirement
 */
export const meetsMinimumPrice = (price, currency = 'GBP') => {
  const minimum = getMinimumPrice(currency);
  return typeof price === 'number' && !isNaN(price) && price >= minimum;
};

